import { defaultRangeExtractor, type Range, useVirtualizer } from '@tanstack/react-virtual';
import { memo, type ReactNode, useCallback, useState } from 'react';

// Tray panel is `h-[min(70vh,40rem)]` by `w-[480px]`. Paint rows before ResizeObserver reports.
const INITIAL_SCROLL_RECT = { height: 640, width: 480 };

const readFocusedRowIndex = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return null;
  }

  const row = target.closest('[data-index]');

  if (!(row instanceof HTMLElement)) {
    return null;
  }

  const index = Number(row.dataset.index);

  return Number.isInteger(index) ? index : null;
};

export type MainSideNavOverlayTrayVirtualizedListProps<Row> = {
  estimateSize: (row: Row) => number;
  getRowKey: (row: Row) => string;
  renderRow: (row: Row) => ReactNode;
  rows: readonly Row[];
  testId: string;
};

const MainSideNavOverlayTrayVirtualizedListComponent = <Row,>({
  estimateSize,
  getRowKey,
  renderRow,
  rows,
  testId,
}: MainSideNavOverlayTrayVirtualizedListProps<Row>) => {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  const getItemKey = useCallback(
    (index: number) => {
      const row = rows[index];

      return row ? getRowKey(row) : index;
    },
    [getRowKey, rows],
  );

  const estimateRowSize = useCallback(
    (index: number) => {
      const row = rows[index];

      return row ? estimateSize(row) : 0;
    },
    [estimateSize, rows],
  );

  const rangeExtractor = useCallback(
    (range: Range) => {
      const visibleIndexes = defaultRangeExtractor(range);
      // Scroll updates this range while the focused node is still mounted.
      const activeElement = scrollElement?.contains(document.activeElement) ? document.activeElement : null;
      const focusedIndex = readFocusedRowIndex(activeElement);

      if (
        focusedIndex === null ||
        focusedIndex >= rows.length ||
        (focusedIndex >= range.startIndex && focusedIndex <= range.endIndex)
      ) {
        return visibleIndexes;
      }

      return [...visibleIndexes, focusedIndex].sort((a, b) => a - b);
    },
    [rows.length, scrollElement],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: estimateRowSize,
    getItemKey,
    getScrollElement: () => scrollElement,
    initialRect: INITIAL_SCROLL_RECT,
    overscan: 8,
    // Former scroll-container `py-3`. Included in row offsets so `scrollToIndex` matches the scroll element.
    paddingEnd: 12,
    paddingStart: 12,
    rangeExtractor,
  });

  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid={testId} ref={setScrollElement}>
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];

          if (!row) {
            return null;
          }

          return (
            <div
              className="absolute top-0 left-0 w-full"
              data-index={virtualRow.index}
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {renderRow(row)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

MainSideNavOverlayTrayVirtualizedListComponent.displayName = 'MainSideNavOverlayTrayVirtualizedList';

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const MainSideNavOverlayTrayVirtualizedList = memo(
  MainSideNavOverlayTrayVirtualizedListComponent,
) as typeof MainSideNavOverlayTrayVirtualizedListComponent;
